import firebase from '../config/firebase.config';

const database = firebase.firestore();
const { FieldValue } = firebase.firestore;

/**
 * Get all alerts for a user
 * Alerts are stored in membersDatabase/{userId}/alerts subcollection
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @param {boolean} options.includeRead - Include read alerts (default: false)
 * @param {number} options.limit - Maximum number of alerts to fetch (default: 50)
 * @param {string} options.orderBy - Field to order by (default: 'createdAt')
 * @param {string} options.orderDirection - Order direction: 'asc' or 'desc' (default: 'desc')
 * @returns {Promise} Array of alerts
 */
async function getUserAlerts(userId, options = {}) {
  try {
    console.log('🔔 alertsService.getUserAlerts: Called', { userId, options });

    const {
      includeRead = false,
      limit = 50,
      orderBy = 'createdAt',
      orderDirection = 'desc',
    } = options;

    if (!userId) {
      throw new Error('User ID is required');
    }

    // Query alerts from user's subcollection: membersDatabase/{userId}/alerts
    let query = database
      .collection('membersDatabase')
      .doc(userId)
      .collection('alerts');

    // Filter by read status if needed
    if (!includeRead) {
      query = query.where('isRead', '==', false);
      console.log(
        '🔔 alertsService.getUserAlerts: Filtering for unread alerts only'
      );
    } else {
      console.log('🔔 alertsService.getUserAlerts: Including read alerts');
    }

    // Order by specified field
    query = query.orderBy(orderBy, orderDirection);

    // Limit results
    query = query.limit(limit);

    console.log(
      '🔔 alertsService.getUserAlerts: Executing query on membersDatabase/{userId}/alerts...'
    );
    const snapshot = await query.get();
    console.log('🔔 alertsService.getUserAlerts: Query executed', {
      snapshotSize: snapshot.size,
      docsCount: snapshot.docs.length,
    });

    const alerts = snapshot.docs.map((doc) => {
      const data = doc.data();
      const alert = {
        id: doc.id,
        ...data,
      };
      console.log('🔔 alertsService.getUserAlerts: Alert data', {
        id: alert.id,
        title: alert.title,
        isRead: alert.isRead,
      });
      return alert;
    });

    console.log('🔔 alertsService.getUserAlerts: Returning alerts', {
      total: alerts.length,
      unreadCount: alerts.filter((a) => !a.isRead).length,
      readCount: alerts.filter((a) => a.isRead).length,
    });

    return {
      success: true,
      data: alerts,
      total: alerts.length,
    };
  } catch (error) {
    console.error('🔔 alertsService.getUserAlerts: Error', error);
    return {
      success: false,
      data: [],
      total: 0,
      error: error.message || 'Failed to fetch alerts',
    };
  }
}

/**
 * Mark multiple alerts as read
 * Alerts are stored in membersDatabase/{userId}/alerts subcollection
 * @param {string[]} alertIds - Array of alert IDs
 * @param {string} userId - User ID
 * @returns {Promise} Success status
 */
async function markAlertsAsRead(alertIds, userId) {
  try {
    console.log('🔔 alertsService.markAlertsAsRead: Called', {
      alertIds,
      userId,
    });

    if (!alertIds || !Array.isArray(alertIds) || alertIds.length === 0) {
      throw new Error('Alert IDs array is required');
    }

    if (!userId) {
      throw new Error('User ID is required');
    }

    const batch = database.batch();
    // Alerts are in membersDatabase/{userId}/alerts/{alertId}
    const alertRefs = alertIds.map((id) =>
      database
        .collection('membersDatabase')
        .doc(userId)
        .collection('alerts')
        .doc(id)
    );

    // Get all alerts to verify they exist
    console.log(
      '🔔 alertsService.markAlertsAsRead: Fetching alert documents...'
    );
    const alertDocs = await Promise.all(alertRefs.map((ref) => ref.get()));
    console.log('🔔 alertsService.markAlertsAsRead: Fetched alert documents', {
      count: alertDocs.length,
      exists: alertDocs.map((doc) => doc.exists),
    });

    alertDocs.forEach((alertDoc, index) => {
      if (alertDoc.exists) {
        const alertData = alertDoc.data();

        console.log('🔔 alertsService.markAlertsAsRead: Processing alert', {
          alertId: alertIds[index],
          currentIsRead: alertData.isRead,
        });

        // Simple update: just set isRead to true (no readBy array needed in new structure)
        batch.update(alertRefs[index], {
          isRead: true,
          readAt: firebase.firestore.FieldValue.serverTimestamp(),
        });

        console.log(
          '🔔 alertsService.markAlertsAsRead: Marking alert as read',
          {
            alertId: alertIds[index],
          }
        );
      } else {
        console.warn(
          '🔔 alertsService.markAlertsAsRead: Alert document does not exist',
          {
            alertId: alertIds[index],
            userId,
          }
        );
      }
    });

    console.log(
      '🔔 alertsService.markAlertsAsRead: Committing batch update...'
    );
    await batch.commit();
    console.log('🔔 alertsService.markAlertsAsRead: Batch commit successful');

    return {
      success: true,
      message: 'Alerts marked as read',
    };
  } catch (error) {
    console.error('🔔 alertsService.markAlertsAsRead: Error', error);
    return {
      success: false,
      error: error.message || 'Failed to mark alerts as read',
    };
  }
}

/**
 * Get unread alerts count for a user
 * Alerts are stored in membersDatabase/{userId}/alerts subcollection
 * @param {string} userId - User ID
 * @returns {Promise} Count of unread alerts
 */
async function getUnreadAlertsCount(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const snapshot = await database
      .collection('membersDatabase')
      .doc(userId)
      .collection('alerts')
      .where('isRead', '==', false)
      .get();

    return {
      success: true,
      count: snapshot.size,
    };
  } catch (error) {
    console.error('Error getting unread alerts count:', error);
    return {
      success: false,
      count: 0,
      error: error.message || 'Failed to get unread alerts count',
    };
  }
}

/**
 * Format date for display
 * @param {Object|string|Date} timestamp - Firestore timestamp or date
 * @returns {string} Formatted date string
 */
function formatDate(timestamp) {
  try {
    if (!timestamp) return 'N/A';

    let date;
    if (timestamp.toDate) {
      // Firestore timestamp
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return 'N/A';
    }

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}

/**
 * Get severity badge color
 * @param {string} severity - Alert severity
 * @returns {string} CSS class name for badge color
 */
function getSeverityBadgeColor(severity) {
  const severityColors = {
    high: 'danger',
    medium: 'warning',
    low: 'info',
  };

  return severityColors[severity] || 'secondary';
}

/**
 * Get severity text
 * @param {string} severity - Alert severity
 * @returns {string} Human-readable severity
 */
function getSeverityText(severity) {
  const severityTexts = {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };

  return (
    severityTexts[severity] ||
    severity?.charAt(0).toUpperCase() + severity?.slice(1) ||
    'Unknown'
  );
}

/**
 * Get alert type icon class
 * @param {string} type - Alert type
 * @returns {string} Icon class name
 */
function getAlertTypeIcon(type) {
  const typeIcons = {
    productivity: 'ni ni-chart-bar-32',
    login: 'ni ni-key-25',
    attendance: 'ni ni-time-alarm',
    other: 'ni ni-notification-70',
  };

  return typeIcons[type] || 'ni ni-notification-70';
}

/**
 * Fetch recent alerts for a specific member, queried from the admin's subcollection.
 * Alerts are stored under membersDatabase/{adminUID}/alerts with userId = memberUID.
 */
async function getMemberAlertsForAdmin(adminUID, memberUID, options = {}) {
  try {
    const { limit: limitCount = 5 } = options;
    if (!adminUID || !memberUID) return { success: true, data: [] };

    const snapshot = await database
      .collection('membersDatabase')
      .doc(adminUID)
      .collection('alerts')
      .where('userId', '==', memberUID)
      .orderBy('createdAt', 'desc')
      .limit(limitCount)
      .get();

    return {
      success: true,
      data: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
    };
  } catch (error) {
    console.error('getMemberAlertsForAdmin error:', error);
    return { success: false, data: [] };
  }
}

/**
 * Fetch recent alerts for a team via collection group query.
 * Requires [teamId ASC, createdAt DESC] collection group index on alerts.
 */
async function getTeamRecentAlerts(teamId, options = {}) {
  try {
    const { limit: limitCount = 5 } = options;
    if (!teamId) return { success: true, data: [] };

    const snapshot = await database
      .collectionGroup('alerts')
      .where('teamId', '==', teamId)
      .where('isPrimary', '==', true)
      .orderBy('createdAt', 'desc')
      .limit(limitCount)
      .get();

    return {
      success: true,
      data: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
    };
  } catch (error) {
    console.error('getTeamRecentAlerts error:', error);
    return { success: false, data: [] };
  }
}

async function markAllAlertsAsRead(userId) {
  try {
    const snapshot = await database
      .collection('membersDatabase')
      .doc(userId)
      .collection('alerts')
      .where('isRead', '==', false)
      .get();

    const batch = database.batch();
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, {
        isRead: true,
      });
    });
    await batch.commit();
    return {
      success: true,
      message: 'All alerts marked as read',
    };
  } catch (error) {
    console.error('Error marking all alerts as read:', error);
    return {
      success: false,
      error: error.message || 'Failed to mark all alerts as read',
    };
  }
}
export default {
  getUserAlerts,
  getMemberAlertsForAdmin,
  getTeamRecentAlerts,
  markAlertsAsRead,
  getUnreadAlertsCount,
  formatDate,
  getSeverityBadgeColor,
  getSeverityText,
  getAlertTypeIcon,
  markAllAlertsAsRead,
};
